import { useQueryClient } from '@tanstack/react-query';

import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { collectData } from 'components/helpers/reactQuery';

const ADDABLE_COUNT_SEARCHES = {
  roleIds: {
    paramKey: 'company_role_search',
    path: 'company_role_searches',
    queryKeySegment: 'companyRoleSearches',
  },
  teamIds: {
    paramKey: 'team_search',
    path: 'team_searches',
    queryKeySegment: 'teamSearches',
  },
};

export default function useAddablePersonnelCounts() {
  const { projectId } = useProjectPersonnelContext();

  const queryClient = useQueryClient();

  const cachedAddableCounts = ({ resourceType }) =>
    new Map(
      collectData({
        queryClient,
        queryKey: [
          'projects',
          projectId,
          ADDABLE_COUNT_SEARCHES[resourceType].queryKeySegment,
        ],
      }).map(
        ({
          id: resourceId,
          meta: { alreadyAddedPersonnelCount, personnelCount },
        }) => [resourceId, personnelCount - alreadyAddedPersonnelCount],
      ),
    );

  const addableCounts = ({ resourceIds, resourceType }) => {
    const cachedCounts = cachedAddableCounts({ resourceType });

    return new Map(
      [...new Set(resourceIds)].map((resourceId) => [
        resourceId,
        cachedCounts.get(resourceId),
      ]),
    );
  };

  const fetchAddableCounts = async ({ resourceIds, resourceType }) => {
    const cachedCounts = cachedAddableCounts({ resourceType });

    const resourceIdsWithoutAddableCounts = [...new Set(resourceIds)].filter(
      (resourceId) => !cachedCounts.has(resourceId),
    );

    if (resourceIdsWithoutAddableCounts.length === 0) return;

    const { paramKey, path, queryKeySegment } =
      ADDABLE_COUNT_SEARCHES[resourceType];

    const params = {
      filter: { ids: { in: resourceIdsWithoutAddableCounts } },
      page: 'all',
    };

    await queryClient.fetchQuery({
      queryKey: ['projects', projectId, queryKeySegment, params],
      queryFn: async () => {
        const response = await axios.post(`/projects/${projectId}/${path}`, {
          [paramKey]: params,
        });

        return response.data;
      },
    });
  };

  return { addableCounts, fetchAddableCounts };
}
